// ---------------------------------------------------------------------------
// 電話番号の区切り（2026-09-19 射場さん「電話・FAX も自動で次の欄へ進めないか。携帯なら 090・080・050・070、
// 大阪は 06、東京は 03 を打つと次の欄へ」）。
//
// 日本の番号は頭の数字で区切り方が決まる：固定電話は 0＋市外局番（1〜4桁）＋市内局番（1〜4桁）＋4桁の10桁、
// 携帯・IP は 3-4-4 の11桁、フリーダイヤルは 0120-xxx-xxx など。
// 市外局番の桁数は総務省の番号表から作った PHONE_AREA_PREFIXES で引く（scripts/phone-area-codes.py）。
//   「03」「06」→ 2桁で決まる／「090」→ 3桁／「0422」→ 4桁
//   「042」は 042-6xx（八王子）・0422（武蔵野）・04-29xx（所沢）に分かれるので、次の数字まで待つ
// 画面（PhoneFields）はこれで「自動で次の欄へ」、API は「ハイフン無しの番号にハイフンを入れる」（formatPhone）。
// ---------------------------------------------------------------------------
import { PHONE_AREA_PREFIXES, PHONE_PATTERNS } from "./phoneAreaCodes.js";

/** 区切りの桁数（例：[2, 4, 4]＝06-6429-4800） */
export type PhoneGroups = readonly [number, number, number];

const TABLE = new Map<string, PhoneGroups>(
  PHONE_AREA_PREFIXES.split(",").map((e) => {
    const [p, i] = e.split(":");
    return [p!, PHONE_PATTERNS[Number(i)]!] as const;
  }),
);
const LONGEST = Math.max(...[...TABLE.keys()].map((k) => k.length));

/** 全角の数字・ハイフン・括弧を半角に */
export function toHalfPhone(s: string): string {
  return s
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[－‐−ー―—–]/g, "-")
    .replace(/（/g, "(")
    .replace(/）/g, ")");
}

/**
 * 頭の数字（半角数字だけ）から区切り方。まだ決まらなければ null。
 * 長い頭の数字から引く（「060」携帯は「06」大阪より、「0800」フリーダイヤルは「080」携帯より先）
 */
export function phoneGroupsOf(digits: string): PhoneGroups | null {
  for (let k = Math.min(LONGEST, digits.length); k >= 2; k--) {
    const g = TABLE.get(digits.slice(0, k));
    if (g) return g;
  }
  return null;
}

/**
 * 1つ目の欄（市外局番）に打った数字だけから、2つ目・3つ目の欄の桁数を決める（表で決まらず、使う人が自分で次の欄へ移ったとき）。
 * 固定電話は合わせて10桁なので 市内局番＝6−市外局番の桁数、最後は4桁。
 */
export function phoneGroupsForArea(area: string): PhoneGroups | null {
  const g = phoneGroupsOf(area);
  if (g && g[0] === area.length) return g;
  if (/^0[1-9][0-9]{0,3}$/.test(area) && area.length >= 2 && area.length <= 5) return [area.length, 6 - area.length, 4];
  return null;
}

/** 数字の並びを区切り方で3つに分ける（足りなければ後ろが空） */
function cut(d: string, g: PhoneGroups): [string, string, string] {
  return [d.slice(0, g[0]), d.slice(g[0], g[0] + g[1]), d.slice(g[0] + g[1])];
}

/**
 * 保存してある番号を3つの欄に分ける。
 *   「06-6429-4800」「06(6429)4800」→ 区切りどおり／「0664294800」→ 表で区切る／分からなければ1つ目にまとめて入れる
 */
export function splitPhone(value: string): [string, string, string] {
  const s = toHalfPhone(value).replace(/[()\s　]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  if (!s) return ["", "", ""];
  if (s.includes("-")) {
    const p = s.split("-");
    return [p[0] ?? "", p[1] ?? "", p.slice(2).join("")];
  }
  const d = s.replace(/[^0-9]/g, "");
  const g = phoneGroupsOf(d);
  return g ? cut(d, g) : [s, "", ""];
}

/** 3つの欄を1つの番号に（空の欄は飛ばす）。「06」「6429」「4800」→「06-6429-4800」 */
export const joinPhone = (parts: readonly string[]): string =>
  parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join("-");

/**
 * 保存する形に整える：全角→半角、括弧・空白はハイフンに、ハイフン無しで桁がそろっていれば区切る。
 *   「０６６４２９４８００」→「06-6429-4800」／「06(6429)4800」→「06-6429-4800」／「090 1234 5678」→「090-1234-5678」
 * 桁がそろわない・知らない頭の数字はそのまま（勝手に直さない）
 */
export function formatPhone(value: string): string {
  const s = toHalfPhone(value).trim().replace(/[()\s　]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  if (!s || s.includes("-") || !/^[0-9]+$/.test(s)) return s;
  const g = phoneGroupsOf(s);
  return g && s.length === g[0] + g[1] + g[2] ? cut(s, g).join("-") : s;
}

// ---------------------------------------------------------------------------
// 1つの欄の電話・郵便番号（#153、docs/62 §5。自社情報の iOS 風の入力の行）。打った（貼った）文字から、欄に見せる形を作る。
// 分かれた欄（PhoneFields・AddressFields の 3桁＋4桁）は顧客・見積・請求書に今のまま残す（その画面を iOS 風にするときに移す）。
// ---------------------------------------------------------------------------

/**
 * 1つの欄の電話：区切りの表で決まった所に「-」を入れる（区切りの直後まで打ったら末尾にも「-」＝「03-1234-」）。
 *   決まらないうちは入れない（「042」）。表で決まらず、使う人が自分で「-」を打ったら、その前の数字を市外局番として phoneGroupsForArea で決める。
 *   それでも決まらなければ打ったとおり（「-」は続けない）。全角・括弧・空白は半角と「-」に、数字と「-」のほかは捨てる。
 *   「06-6429-4800」「0664294800」「０６（６４２９）４８００」はどれも「06-6429-4800」
 */
export function typePhone(raw: string): string {
  const t = toHalfPhone(raw)
    .replace(/[()\s　]+/g, "-")
    .replace(/[^0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-/, "");
  const d = t.replace(/-/g, "");
  if (!d) return "";
  const g = phoneGroupsOf(d) ?? (t.includes("-") ? phoneGroupsForArea(t.split("-")[0]!) : null);
  if (!g) return t;
  const [a, b, c] = g;
  const total = a + b + c;
  const [p0, p1, p2] = cut(d, g);
  if (d.length < a) return p0;
  if (d.length === a) return d.length < total ? `${p0}-` : p0;
  if (d.length < a + b) return `${p0}-${p1}`;
  if (d.length === a + b) return d.length < total ? `${p0}-${p1}-` : `${p0}-${p1}`;
  return `${p0}-${p1}-${p2}`;
}

/** 欄に見せる形から、保存する形（末尾の「-」を外す）。「03-1234-」→「03-1234」 */
export const phoneValueOf = (text: string): string => text.replace(/-+$/, "");

/** 1つの欄の郵便番号：3桁打つと「-」、7桁で「101-0047」（8桁目からは捨てる） */
export function typeZip(raw: string): string {
  const d = toHalfPhone(raw).replace(/[^0-9]/g, "").slice(0, 7);
  if (d.length < 3) return d;
  return `${d.slice(0, 3)}-${d.slice(3)}`;
}
